import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Card, EmptyState } from "@/components/Shell";
import StatusBadge from "@/components/StatusBadge";
import FileUpload from "@/components/FileUpload";
import { Plus, RotateCcw, Check } from "lucide-react";

export default function Recoveries() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [err, setErr] = useState("");
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [form, setForm] = useState({
    project_id:"", customer_name:"", site_address:"", recovered_from:"CE Side", recovery_date:"",
    returned_to_warehouse_id:"", courier_details:"", remark:"",
    items:[{ product_id:"", quantity:1, condition:"Working", serial_numbers:"" }],
  });

  const load = () => api.get("/recoveries").then(r => setItems(r.data)).catch(e=>setErr(formatApiError(e)));
  useEffect(() => { load(); api.get("/products").then(r=>setProducts(r.data)); api.get("/warehouses").then(r=>setWarehouses(r.data)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        returned_to_warehouse_id: form.returned_to_warehouse_id || null,
        items: form.items.map(it => ({
          product_id: it.product_id, quantity: parseInt(it.quantity)||0,
          condition: it.condition,
          serial_numbers: (it.serial_numbers||"").split(",").map(s=>s.trim()).filter(Boolean),
        })),
        attachments: attachedFiles.map(f => f.id),
      };
      await api.post("/recoveries", payload);
      setShowCreate(false);
      setForm({ project_id:"", customer_name:"", site_address:"", recovered_from:"CE Side", recovery_date:"",
        returned_to_warehouse_id:"", courier_details:"", remark:"",
        items:[{ product_id:"", quantity:1, condition:"Working", serial_numbers:"" }] });
      load();
    } catch (e2) { setErr(formatApiError(e2)); }
  };

  const receive = async (id) => {
    const wh = prompt("Warehouse ID to receive into?\n\n" + warehouses.map(w => `${w.name} → ${w.id}`).join("\n"));
    if (!wh) return;
    try { await api.post(`/recoveries/${id}/receive`, { warehouse_id: wh, remark: "" }); load(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <div className="p-4 md:p-8" data-testid="recoveries-page">
      <PageHeader title="Material Recoveries" subtitle="Track recovered units from site back to warehouse."
        action={<button onClick={()=>setShowCreate(s=>!s)} data-testid="new-recovery-btn" className="inline-flex items-center gap-2 bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold text-sm py-2 px-4 rounded-md"><Plus size={14}/> New Recovery</button>}
      />

      {showCreate && (
        <Card className="p-5 mb-6" data-testid="recovery-form">
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input required placeholder="Project / Circuit ID" value={form.project_id} onChange={e=>setForm(f=>({...f, project_id:e.target.value}))} className="inp" data-testid="rec-project" />
              <input placeholder="Customer Name" value={form.customer_name} onChange={e=>setForm(f=>({...f, customer_name:e.target.value}))} className="inp" />
              <input placeholder="Site Address" value={form.site_address} onChange={e=>setForm(f=>({...f, site_address:e.target.value}))} className="inp md:col-span-2" />
              <select value={form.recovered_from} onChange={e=>setForm(f=>({...f, recovered_from:e.target.value}))} className="inp">
                {["BTS Side","CE Side","Customer Premise","POP","Other"].map(x=><option key={x}>{x}</option>)}
              </select>
              <input type="date" value={form.recovery_date} onChange={e=>setForm(f=>({...f, recovery_date:e.target.value}))} className="inp" />
              <select value={form.returned_to_warehouse_id} onChange={e=>setForm(f=>({...f, returned_to_warehouse_id:e.target.value}))} className="inp">
                <option value="">Return To Warehouse (optional)</option>
                {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
              <input placeholder="Courier Details (if not direct)" value={form.courier_details} onChange={e=>setForm(f=>({...f, courier_details:e.target.value}))} className="inp" />
              <textarea placeholder="Remark" value={form.remark} onChange={e=>setForm(f=>({...f, remark:e.target.value}))} className="inp md:col-span-2" rows={2}></textarea>
            </div>
            {form.items.map((it,i)=>(
              <div key={i} className="grid grid-cols-12 gap-2">
                <select value={it.product_id} onChange={e=>{const a=[...form.items];a[i]={...a[i],product_id:e.target.value};setForm(f=>({...f,items:a}));}} className="inp col-span-12 md:col-span-4" data-testid={`rec-product-${i}`}>
                  <option value="">Product</option>
                  {products.map(p => <option key={p.id} value={p.id}>{p.sku} — {p.name}</option>)}
                </select>
                <input type="number" value={it.quantity} onChange={e=>{const a=[...form.items];a[i]={...a[i],quantity:e.target.value};setForm(f=>({...f,items:a}));}} className="inp col-span-3 md:col-span-1" />
                <select value={it.condition} onChange={e=>{const a=[...form.items];a[i]={...a[i],condition:e.target.value};setForm(f=>({...f,items:a}));}} className="inp col-span-4 md:col-span-2">
                  {["Working","Faulty","Damaged","Repairable","Scrap","Unknown"].map(x=><option key={x}>{x}</option>)}
                </select>
                <input placeholder="Serial #s" value={it.serial_numbers} onChange={e=>{const a=[...form.items];a[i]={...a[i],serial_numbers:e.target.value};setForm(f=>({...f,items:a}));}} className="inp col-span-5 md:col-span-5" />
              </div>
            ))}
            <button type="button" onClick={()=>setForm(f=>({...f, items:[...f.items,{product_id:"",quantity:1,condition:"Working",serial_numbers:""}]}))} className="text-xs font-semibold text-[#F97316]">+ Add item</button>
            <div className="pt-3 border-t border-zinc-100">
              <div className="text-[11px] uppercase tracking-widest font-bold text-zinc-700 mb-2">Site Photos / Attachments</div>
              <FileUpload value={attachedFiles} onChange={setAttachedFiles} testId="recovery-attach" />
            </div>
            {err && <div className="text-rose-600 text-sm">{err}</div>}
            <button type="submit" data-testid="rec-save-btn" className="bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold text-sm py-2 px-4 rounded-md">Submit Recovery</button>
          </form>
        </Card>
      )}

      {err && <div className="text-rose-600 text-sm mb-3">{err}</div>}
      {items.length === 0 ? <EmptyState title="No recoveries yet" icon={RotateCcw} /> :
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm data-table">
              <thead className="bg-zinc-50">
                <tr>
                  <th className="px-4 py-3 text-left">Project/Circuit</th>
                  <th className="px-4 py-3 text-left">Customer</th>
                  <th className="px-4 py-3 text-left">Engineer</th>
                  <th className="px-4 py-3 text-left">From</th>
                  <th className="px-4 py-3 text-left">Items</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  {["admin","warehouse_manager"].includes(user.role) && <th className="px-4 py-3 text-left">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {items.map(r => (
                  <tr key={r.id} className="border-t border-zinc-100 hover:bg-zinc-50">
                    <td className="px-4 py-3 num"><Link to={`/recoveries/${r.id}`} className="text-[#F97316] font-semibold hover:underline" data-testid={`recovery-link-${r.id}`}>{r.project_id || "—"}</Link></td>
                    <td className="px-4 py-3">{r.customer_name || "—"}</td>
                    <td className="px-4 py-3">{r.engineer_name}</td>
                    <td className="px-4 py-3 text-zinc-500">{r.recovered_from}</td>
                    <td className="px-4 py-3 num">{r.items?.length || 0}</td>
                    <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                    {["admin","warehouse_manager"].includes(user.role) && (
                      <td className="px-4 py-3">
                        {r.status === "In Transit Recovery" && (
                          <Link to={`/recoveries/${r.id}`} data-testid={`receive-rec-${r.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:underline">
                            <Check size={13} /> Receive
                          </Link>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      }
      <style>{`.inp { width:100%; border:1px solid #E4E4E7; background:#fff; border-radius:.375rem; padding:.5rem .75rem; font-size:.85rem; outline:none } .inp:focus { box-shadow: 0 0 0 2px #F97316; border-color: transparent }`}</style>
    </div>
  );
}
